import {
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@repo/shadcn';
import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { CiMaximize2, CiMinimize2 } from 'react-icons/ci';
import { toast } from 'sonner';

import { useFullscreen } from '@/hooks/use-fullscreen';

import { CartAction } from './cart-action';
import CartAmount from './cart-amount';
import CartList from './cart-list';
import { useCreateSale } from './create-sale.hook';
import SaleCustomer from './sale-customer';
import { SaleProductAutoComplete } from './sale-product-autocomplete';
import SaleSkeleton from './sale-skelton';

export const CreateNewSale = () => {
    const {
        changeSelectedProduct,
        productValue,
        handleSubmit,
        cartData,
        cartDataLoading,
        register,
        setProductValue,
        isError,
        isSuccess,
        saleApiError,
        control,
        clearCartApi,
        clearCartLoading,
        // clearCart,
        setProductValue: setValue,
        addToCartLoading,
        addToCartError,
    } = useCreateSale();

    const { isFullscreen, toggleFullscreen, fullscreenRef } = useFullscreen();
    useEffect(() => {
        const handleKeyPress = (event: any) => {
            if (event.ctrlKey && event.key === 'space') {
                event.preventDefault();
                toggleFullscreen();
            }
        };
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [toggleFullscreen]);

    if (addToCartError.data?.data.message) {
        toast.error(addToCartError.data?.data.message, {
            style: {
                background: 'red',
                color: 'white',
            },
        });
    }

    const watchedDiscountValue = useWatch({ control, name: 'discount_value' });
    const watchedDiscountType = useWatch({ control, name: 'discount_type' });

    useEffect(() => {
        let updatedValue = watchedDiscountValue;
        if (!cartData?.data.info.sub_total_price) return;

        if (
            watchedDiscountType === 'fixed' &&
            watchedDiscountValue > cartData?.data.info.sub_total_price
        ) {
            updatedValue = cartData?.data.info.sub_total_price;
        }

        if (
            watchedDiscountType === 'percentage' &&
            watchedDiscountValue > 100
        ) {
            updatedValue = 100;
        }

        if (updatedValue !== watchedDiscountValue) {
            setValue('discount_value', updatedValue);
        }
    }, [
        watchedDiscountValue,
        watchedDiscountType,
        cartData?.data.info.sub_total_price,
        setValue,
    ]);
    if (isError) {
        return (
            <p>
                {' '}
                {saleApiError.success
                    ? saleApiError.data.data.message
                    : 'Something went wrong'}{' '}
            </p>
        );
    }

    if (cartDataLoading) {
        return <SaleSkeleton />;
    }
    if (!isError && !cartDataLoading && isSuccess && cartData) {
        return (
            <div
                ref={fullscreenRef}
                style={{ isolation: 'isolate' }}
                className={`relative ${isFullscreen ? 'fixed inset-0 overflow-auto bg-background p-4' : ''}`}
            >
                <form onSubmit={handleSubmit}>
                    <div className=" relative  grid w-full grid-cols-7 flex-wrap gap-3 ">
                        <div className="col-span-5 w-full">
                            <div className="col-span-4 grid h-fit grid-cols-6  gap-3 rounded-lg  border-[1px] bg-primary  p-4 ">
                                <div className="col-span-8">
                                    <SaleCustomer />
                                </div>

                                <div className="col-span-3  ">
                                    <Label className="text-white">
                                        Select Product
                                    </Label>

                                    <SaleProductAutoComplete
                                        value={productValue}
                                        changeSelectedProduct={
                                            changeSelectedProduct
                                        }
                                        setValue={setProductValue}
                                    />
                                </div>

                                <div className="col-span-1">
                                    <Label className="text-white">
                                        Quantity
                                    </Label>
                                    <Input
                                        className="mt-2 bg-slate-200"
                                        placeholder="Quantity"
                                        {...register('quantity')}
                                    />
                                </div>
                                <div className="col-span-1 ">
                                    <Label className="text-white">Price</Label>
                                    <Input
                                        placeholder="Price"
                                        className="mt-2 w-full bg-slate-200"
                                        {...register('price')}
                                    />
                                </div>
                                <div className="col-span-1 ">
                                    <Label className="text-white">
                                        Discount
                                    </Label>

                                    <div className=" mt-2 flex w-full  justify-between rounded-md border-[1px]  bg-white">
                                        <Input
                                            className="max-w-[50px] border-none p-0 text-center text-[18px] text-primary focus:border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            {...register('discount_value', {
                                                required: true,
                                                valueAsNumber: true,
                                                validate: (value) =>
                                                    !isNaN(value),
                                            })}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={(e) => {
                                                const parsed = parseFloat(
                                                    e.target.value,
                                                );
                                                if (isNaN(parsed)) {
                                                    setValue(
                                                        'discount_value',
                                                        0,
                                                    );
                                                }
                                            }}
                                        />

                                        <Controller
                                            name="discount_type"
                                            control={control}
                                            render={({ field }: any) => (
                                                <Select
                                                    value={field.value}
                                                    onValueChange={(value) =>
                                                        field.onChange(value)
                                                    }
                                                >
                                                    <SelectTrigger className="w-[60px] border-none bg-slate-200 text-[18px] font-semibold text-primary focus:border-none focus:ring-0 focus:ring-offset-0">
                                                        <SelectValue placeholder="Type" />
                                                    </SelectTrigger>
                                                    <SelectContent className="!w-[60px] font-semibold">
                                                        <SelectItem
                                                            value="fixed"
                                                            className="px-4"
                                                        >
                                                            {' '}
                                                            ৳
                                                        </SelectItem>
                                                        <SelectItem
                                                            value="percentage"
                                                            className="px-4"
                                                        >
                                                            %
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </div>
                                </div>
                                <Button
                                    className="col-span-1 items-end self-end"
                                    variant={'secondary'}
                                    type="submit"
                                >
                                    {addToCartLoading
                                        ? 'Adding...'
                                        : 'Add More'}
                                </Button>
                            </div>
                            <CartAction
                                cartData={cartData.data.items}
                                clearCartApi={clearCartApi}
                                clearCartLoading={clearCartLoading}
                            />
                            <CartList
                                isFullscreen={isFullscreen}
                                cartData={cartData.data.items}
                                cartDataLoading={cartDataLoading}
                            />
                        </div>
                        <div className="relative col-span-2 w-full">
                            <Button
                                type="button"
                                variant="secondary"
                                size="icon"
                                className="absolute right-4 top-4 z-10"
                                onClick={toggleFullscreen}
                            >
                                {isFullscreen ? (
                                    <CiMinimize2 className="h-4 w-4" />
                                ) : (
                                    <CiMaximize2 className="h-4 w-4" />
                                )}
                            </Button>
                            <CartAmount cartDetails={cartData.data.info!} />
                            {/* <QuickAction /> */}
                        </div>
                    </div>
                </form>
            </div>
        );
    }
    return 'Something went wrong';
};
